import React, { useState, useEffect, useRef } from 'react';
import { gameService } from '../firebase/gameService';
import { GameState, CurrentQuestion, Question, Player } from '../types/game';
import { GameBoardGrid } from './GameBoardGrid';
import QRCode from 'react-qr-code';
import FinalJeopardy from './FinalJeopardy';
import { FaBolt } from 'react-icons/fa';

interface Props {
  gameId: string;
  playerId?: string;
}

const AudienceView: React.FC<Props> = ({ gameId, playerId }) => {
  const [game, setGame] = useState<GameState | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [wagerAmount, setWagerAmount] = useState<string>('');
  const [finalAnswer, setFinalAnswer] = useState<string>('');
  const [selectedFinalAnswer, setSelectedFinalAnswer] = useState<string | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = gameService.subscribeToGame(gameId, (updatedGame) => {
      setGame(updatedGame);
      // Reset submission state when a new question is revealed
      if (updatedGame.currentQuestion?.question.id !== game?.currentQuestion?.question.id) {
        setHasSubmitted(false);
        setSelectedAnswer(null);
      }
    });

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      unsubscribe();
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [gameId]);

  const handleToggleFullscreen = () => {
    if (!isFullscreen && mainRef.current) {
      mainRef.current.requestFullscreen();
    } else if (isFullscreen) {
      document.exitFullscreen();
    }
  };

  const handleAnswerSubmit = async () => {
    if (!game || !selectedAnswer || !game.currentQuestion || !playerId) return;

    try {
      await gameService.submitAudienceAnswer(
        gameId,
        playerId,
        game.currentQuestion.question.id || '',
        selectedAnswer
      );
      setHasSubmitted(true);
    } catch (error) {
      console.error('Failed to submit answer:', error);
    }
  };

  const handleFinalJeopardyWager = async () => {
    if (!game || !playerId || !wagerAmount) return;
    const amount = parseInt(wagerAmount);
    if (isNaN(amount)) return;

    try {
      await gameService.submitAudienceFinalJeopardyWager(gameId, playerId, amount);
      setWagerAmount('');
      // Add visual feedback
      const wagerInput = document.querySelector('input[type="number"]');
      if (wagerInput) {
        wagerInput.classList.add('bg-green-500/20');
        setTimeout(() => {
          wagerInput.classList.remove('bg-green-500/20');
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to submit wager:', error);
      // Add error feedback
      const wagerInput = document.querySelector('input[type="number"]');
      if (wagerInput) {
        wagerInput.classList.add('bg-red-500/20');
        setTimeout(() => {
          wagerInput.classList.remove('bg-red-500/20');
        }, 1000);
      }
    }
  };

  const handleFinalJeopardyAnswer = async () => {
    if (!game || !playerId || !selectedFinalAnswer) return;

    try {
      await gameService.submitAudienceFinalJeopardyAnswer(gameId, playerId, selectedFinalAnswer);
      setSelectedFinalAnswer(null);
    } catch (error) {
      console.error('Failed to submit answer:', error);
    }
  };

  const getAudiencePlayers = (players: Player[]) => {
    return players.filter(player => player.role === 'audience');
  };

  const getOnStagePlayers = (players: Player[]) => {
    return players.filter(player => player.role === 'player');
  };

  // Type guard to ensure playerId is defined when needed
  const getPlayerWager = (playerId: string | undefined) => {
    if (!playerId) return undefined;
    return game?.finalJeopardy?.wagers?.[playerId];
  };

  // Type guard for current question
  const isCurrentQuestion = (question: CurrentQuestion | null): question is CurrentQuestion => {
    return question !== null;
  };

  // Type guard for game state
  const isGameState = (game: GameState | null): game is GameState => {
    return game !== null;
  };

  if (!isGameState(game)) {
    return <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-brand-yellow text-2xl">Loading...</div>
    </div>;
  }

  const audiencePlayers = getAudiencePlayers(game.players);
  const onStagePlayers = getOnStagePlayers(game.players);
  const currentPlayer = game.players.find(p => p.id === playerId);
  const isPlayAlong = !!(playerId && currentPlayer && currentPlayer.role === 'audience');

  // Type guard for playerId
  const isPlayerIdDefined = (id: string | undefined): id is string => {
    return id !== undefined;
  };

  // Determine round based on host's state (round 1: status 'in_progress' and not advanced, round 2: advanced or status 'finished')
  // We'll infer round by checking if any category has questions with value > 500 (i.e., round 2 values)
  // But better: if host sets a 'round' property in game, use that. For now, mimic host logic:
  const getActiveCategories = () => {
    const active = game.categories.filter(cat => cat.isActive !== false);
    if (game.round === 2) {
      return active.slice(5, 10).length === 5 ? active.slice(5, 10) : active.slice(0, 5);
    }
    return active.slice(0, 5);
  };
  const categories = getActiveCategories();
  const roundValues = game.round === 2 ? [200, 400, 600, 800, 1000] : [100, 200, 300, 400, 500];

  return (
    <div className="min-h-screen bg-black p-8" ref={mainRef}>
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-8">
        {/* Main Content (Board) */}
        <div className="flex-1">
          {/* Show Final Jeopardy for audience if in round 3 */}
          {game.round === 3 && game.finalJeopardy ? (
            <div className="min-h-screen bg-gradient-to-br from-black via-[#181818] to-black flex items-center justify-center p-8 relative overflow-hidden">
              {/* Subtle vignette effect */}
              <div className="absolute inset-0 pointer-events-none" style={{background: 'radial-gradient(ellipse at center, rgba(255,215,64,0.08) 0%,rgba(0,0,0,0.95) 80%)'}} />
              <div className="relative z-10 w-full max-w-3xl mx-auto">
                {/* Final Jeopardy Label */}
                <div className="flex justify-center mb-4">
                  <div className="flex items-center gap-3 bg-gradient-to-r from-[#FFD700] via-[#FFB800] to-[#FFD700] text-black px-8 py-3 rounded-t-2xl shadow-lg uppercase tracking-widest font-extrabold text-lg border-b-4 border-brand-yellow drop-shadow-lg">
                    <FaBolt className="text-xl text-yellow-500 animate-pulse" />
                    Final Jeopardy
                    <FaBolt className="text-xl text-yellow-500 animate-pulse" />
                  </div>
                </div>

                {/* Card */}
                <div className="bg-[#181818] border-4 border-brand-yellow rounded-3xl shadow-2xl px-10 py-12 flex flex-col items-center relative">
                  {/* Category */}
                  <div className="mb-6">
                    <span className="uppercase tracking-widest text-sm font-semibold text-brand-yellow/80 block text-center mb-1">Category</span>
                    <span className="text-3xl md:text-4xl font-extrabold text-brand-yellow text-center block drop-shadow-lg">{game.finalJeopardy.category}</span>
                  </div>

                  {/* Question */}
                  {game.finalJeopardy.isRevealed && !game.finalJeopardy.isAnswered && (
                    <div className="mb-10">
                      <div className="text-white text-2xl md:text-3xl font-bold text-center max-w-2xl mx-auto drop-shadow-lg animate-fade-in">
                        {game.finalJeopardy.question}
                      </div>
                    </div>
                  )}

                  {/* Play-Along Audience: Wager Input */}
                  {isPlayAlong && playerId && !getPlayerWager(playerId)?.amount && (
                    <div className="w-full max-w-md mt-8">
                      <div className="text-white text-lg mb-4 text-center">Enter your wager (max: ${currentPlayer?.score})</div>
                      <div className="flex gap-4">
                        <input
                          type="number"
                          value={wagerAmount}
                          onChange={(e) => setWagerAmount(e.target.value)}
                          placeholder="Enter wager"
                          className="flex-1 p-3 rounded-lg bg-[#333] text-white border border-brand-yellow/40 focus:border-brand-yellow focus:outline-none"
                          min="0"
                          max={currentPlayer?.score}
                        />
                        <button
                          onClick={handleFinalJeopardyWager}
                          disabled={!wagerAmount}
                          className="bg-brand-yellow text-black font-bold px-6 py-3 rounded-lg hover:bg-[#fccd6c]/90 transition-colors disabled:opacity-50"
                        >
                          Submit Wager
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Play-Along Audience: Answer Input */}
                  {isPlayAlong && 
                   playerId &&
                   getPlayerWager(playerId)?.amount && 
                   !getPlayerWager(playerId)?.answer && 
                   game.finalJeopardy.isRevealed && (
                    <div className="w-full max-w-md mt-8">
                      <div className="text-white text-lg mb-4 text-center">Select your answer</div>
                      <div className="grid grid-cols-1 gap-4 mb-4">
                        {['A', 'B', 'C', 'D'].map((choice) => (
                          <button
                            key={choice}
                            onClick={() => setSelectedFinalAnswer(choice)}
                            className={`p-4 text-xl font-bold rounded-lg transition-all ${
                              selectedFinalAnswer === choice
                                ? 'bg-brand-yellow text-black'
                                : 'bg-[#444] text-white hover:bg-[#555]'
                            }`}
                          >
                            {choice}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={handleFinalJeopardyAnswer}
                        disabled={!selectedFinalAnswer}
                        className={`w-full p-3 text-xl font-bold rounded-lg transition-all ${
                          selectedFinalAnswer
                            ? 'bg-brand-yellow text-black hover:bg-brand-yellow/90'
                            : 'bg-[#444] text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Confirm Answer
                      </button>
                    </div>
                  )}

                  {/* Show wager and answer status */}
                  {isPlayAlong && playerId && getPlayerWager(playerId) && (
                    <div className="mt-8 text-center">
                      <div className="text-white text-lg mb-2">
                        Your wager: <span className="text-brand-yellow font-bold">${getPlayerWager(playerId)?.amount}</span>
                      </div>
                      {getPlayerWager(playerId)?.answer && (
                        <div className="text-white text-lg">
                          Your answer: <span className="text-brand-yellow font-bold">{getPlayerWager(playerId)?.answer}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Answer Reveal */}
                  {game.finalJeopardy.isAnswered && (
                    <div className="mt-8 text-center">
                      <div className="text-brand-yellow text-xl font-bold mb-4">Answer:</div>
                      <div className="text-white text-2xl font-bold mb-8">{game.finalJeopardy.answer}</div>
                      {isPlayAlong && playerId && getPlayerWager(playerId)?.isCorrect !== undefined && (
                        <div className="text-white text-xl">
                          {getPlayerWager(playerId)?.isCorrect ? (
                            <span className="text-green-400">Correct! +${getPlayerWager(playerId)?.amount}</span>
                          ) : (
                            <span className="text-red-400">Incorrect! -${getPlayerWager(playerId)?.amount}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show answer when revealed but not yet marked */}
                  {game.finalJeopardy.isRevealed && !game.finalJeopardy.isAnswered && (
                    <div className="mt-8 text-center">
                      <div className="text-brand-yellow text-xl font-bold mb-4">Answer:</div>
                      <div className="text-white text-2xl font-bold mb-8">{game.finalJeopardy.answer}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Question Modal */}
              {game.currentQuestion && isCurrentQuestion(game.currentQuestion) && (
                <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50">
                  <div className="bg-[#222] rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="text-center">
                      <h2 className="text-brand-yellow text-2xl font-bold mb-8">{game.currentQuestion.categoryName}</h2>
                      <div className="text-white text-3xl font-bold mb-8">{game.currentQuestion.question.text}</div>

                      {/* Multiple Choice Options - Only for Play-Along Audience */}
                      {isPlayAlong && game.currentQuestion.question.choices && !game.currentQuestion.question.answered && !hasSubmitted && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                          {game.currentQuestion.question.choices.map((choice: string, index: number) => (
                            <button
                              key={index}
                              onClick={() => setSelectedAnswer(choice)}
                              disabled={game.currentQuestion.question.answered || hasSubmitted}
                              className={`p-4 text-xl font-bold rounded-lg transition-all ${
                                selectedAnswer === choice
                                  ? 'bg-brand-yellow text-black'
                                  : 'bg-[#444] text-white hover:bg-[#555]'
                              }`}
                            >
                              {choice}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Play-Along Audience: Submit Button */}
                      {isPlayAlong && !game.currentQuestion.question.answered && !hasSubmitted && (
                        <button
                          onClick={handleAnswerSubmit}
                          disabled={!selectedAnswer}
                          className={`w-full p-3 sm:p-4 text-base sm:text-xl font-bold rounded-lg transition-all ${
                            selectedAnswer
                              ? 'bg-brand-yellow text-black hover:bg-brand-yellow/90'
                              : 'bg-[#444] text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          Submit Answer
                        </button>
                      )}

                      {/* Answer Reveal (for everyone) */}
                      {game.currentQuestion.question.answered && (
                        <div className="flex flex-col items-center mt-8 w-full">
                          <div className="text-brand-yellow text-lg font-bold mb-2">Answer:</div>
                          <div className="bg-[#acd9b7] text-black text-2xl font-bold rounded-lg px-8 py-6 shadow-lg w-full text-center">
                            {game.currentQuestion.question.answer}
                          </div>
                          {/* Play-Along Audience: Show their answer result */}
                          {isPlayAlong && currentPlayer?.lastAnswer && (
                            <div className="mt-4 text-white">
                              Your answer: {currentPlayer.lastAnswer.answer}
                              <span className={`ml-2 ${currentPlayer.lastAnswer.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                                ({currentPlayer.lastAnswer.isCorrect ? 'Correct!' : 'Incorrect'})
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Hide rest of UI when modal is open */}
              {!game.currentQuestion && (
                <>
                  {/* Categories Grid */}
                  <GameBoardGrid
                    categories={categories}
                    values={roundValues}
                    questions={categories.map(cat => {
                      const questions = game.round === 2 ? cat.questions.slice(5, 10) : cat.questions.slice(0, 5);
                      return questions.map(q => ({
                        answered: q.answered || false,
                        revealed: q.revealed || false,
                        text: q.text
                      }));
                    })}
                  />

                  {/* Audience Leaderboard */}
                  {game.settings.showAudienceLeaderboard && (
                    <div className="bg-[#333333] rounded-xl p-6 shadow-lg">
                      <h2 className="text-2xl font-bold text-brand-yellow mb-4 flex items-center gap-2">
                        <img src="/images/white-glasses.svg" alt="Leaderboard" className="h-6" />
                        Audience Leaderboard
                      </h2>
                      <div className="space-y-2">
                        {audiencePlayers
                          .sort((a, b) => b.score - a.score)
                          .map((player, index) => (
                            <div
                              key={player.id}
                              className={`flex items-center justify-between p-3 rounded-lg ${
                                player.id === playerId ? 'bg-brand-yellow/20' : 'bg-[#444]'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-brand-yellow font-bold w-6">{index + 1}.</span>
                                <span className="text-white font-bold">{player.name}</span>
                              </div>
                              <span className="text-brand-yellow font-bold">${player.score}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Player Sidebar */}
        <div className="w-full md:w-80 flex-shrink-0">
          <div className="bg-[#222] rounded-3xl shadow-2xl p-6 flex flex-col gap-6 sticky top-8">
            {/* QR Code for joining */}
            <div className="flex flex-col items-center mb-6">
              <span className="text-brand-yellow font-bold mb-2 text-lg">Scan to Join This Game</span>
              <div className="bg-white p-2 rounded-xl">
                <QRCode value={`${window.location.origin}/?code=${game.code}`} size={120} />
              </div>
              <span className="text-xs text-gray-400 mt-2">Join as audience & play along</span>
            </div>
            {/* On-Stage Players */}
            <div>
              <h2 className="text-2xl font-extrabold mb-2 text-brand-yellow flex items-center gap-2">
                <img src="/images/white-glasses.svg" alt="Players" className="h-7" />
                On-Stage Players
              </h2>
              <div className="flex flex-col gap-4">
                {onStagePlayers.map(player => (
                  <div key={player.id} className="flex items-center gap-4 bg-[#333] rounded-2xl px-5 py-4 shadow-lg border-2 border-brand-yellow/40">
                    <div className="h-12 w-12 rounded-full bg-[#28332a] flex items-center justify-center">
                      <img src={`/images/${player.avatar}`} alt="Avatar" className="h-8 w-8" />
                    </div>
                    <div className="flex-1">
                      <p className="font-extrabold text-brand-yellow text-lg leading-tight">{player.name}</p>
                      <p className="text-white text-xl font-bold">${player.score}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Audience Leaderboard */}
            {game.settings.showAudienceLeaderboard && (
              <div>
                <h2 className="text-2xl font-extrabold mb-2 text-brand-yellow flex items-center gap-2">
                  <img src="/images/white-glasses.svg" alt="Audience" className="h-7" />
                  Audience Leaderboard
                </h2>
                <div className="flex flex-col gap-4">
                  {audiencePlayers
                    .sort((a, b) => b.score - a.score)
                    .map((player, index) => (
                      <div
                        key={player.id}
                        className="flex items-center gap-5 bg-[#222] rounded-2xl px-6 py-4 shadow-xl border-2 border-brand-yellow/70 min-h-[80px]">
                        <div className="h-16 w-16 rounded-full bg-black flex items-center justify-center border-2 border-brand-yellow shadow-md">
                          <img src={`/images/${player.avatar}`} alt="Avatar" className="h-12 w-12" />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-center items-center text-center">
                          <span className="font-extrabold text-brand-yellow text-2xl leading-tight truncate drop-shadow">{player.name}</span>
                          <span className="text-white text-xl font-bold mt-1">${player.score}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudienceView;